// React imports - Similar to importing package
import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import axios from 'axios';
import './DesignTokens.css';
import './App.css';
import './LuxuryEnhancements.css';
import './RefinedStyling.css';
import './FeaturedSectionFix.css';
import './AuthScreens.css';
import './PagesStyles.css';
import './ChatMarkdown.css';
import './ChatPanel.css';
import './DeviceBreakpoints.css';

import { installFrontendLogging } from './logging';
import { installResponsiveLayout } from './responsive';
import { DeviceProvider } from './DeviceContext';
import { useDevice } from './useDevice';

// Lazy-loaded components — only downloaded when the user navigates to them
const SearchInterface = lazy(() => import('./SearchInterface'));
const SearchResults = lazy(() => import('./SearchResults'));
const ShoppingCart = lazy(() => import('./ShoppingCart'));
const PaymentForm = lazy(() => import('./PaymentForm'));
const BookingConfirmation = lazy(() => import('./BookingConfirmation'));
const TripDetailsScreen = lazy(() => import('./TripDetailsScreen'));
const TravelerDashboard = lazy(() => import('./TravelerDashboard'));
const UserAccount = lazy(() => import('./UserAccount'));
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const SupportCenter = lazy(() => import('./SupportCenter'));
const CurrencyAlertPanel = lazy(() => import('./CurrencyAlertPanel'));
const VisaPrefillPanel = lazy(() => import('./VisaPrefillPanel'));
const DailyItineraryPlanner = lazy(() => import('./DailyItineraryPlanner'));
const NotificationPreferences = lazy(() => import('./NotificationPreferences'));
const NotificationCenter = lazy(() => import('./NotificationCenter'));
const PaymentSuccess = lazy(() => import('./PaymentSuccess'));
const PaymentCancel = lazy(() => import('./PaymentCancel'));
const LoginScreen = lazy(() => import('./LoginScreen'));
const SignupScreen = lazy(() => import('./SignupScreen'));
const ContactScreen = lazy(() => import('./ContactScreen'));
const CommandCenter = lazy(() => import('./command-center/CommandCenter'));

// Eagerly loaded (small, used on every page or critical path)
import Footer from './Footer';
import WorkflowTracker from './WorkflowTracker';
import ChatMessageContent from './ChatMessageContent';
import TripActivityPanel from './TripActivityPanel';
import TripComparisonCard from './TripComparisonCard';
import TripPlanCard from './TripPlanCard';
import { ConnectionBanner, NotificationBell } from './NotificationComponents';
import notificationService from './NotificationService';
import wsService from './WebSocketService';
import NotificationToast from './NotificationToast';
import { I18nProvider, useI18n } from './I18nContext';

// Suspense loading fallback
const LazyFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', color: 'var(--text-muted, #94a3b8)' }}>
    <div style={{ textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto 12px', width: 32, height: 32, border: '3px solid var(--gold-primary, #D4AF37)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <p style={{ fontSize: '0.85rem' }}>Loading...</p>
    </div>
  </div>
);

// Get backend URL from environment - Similar to configuring endpoints in Flutter
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Admin access surface ──────────────────────────────────────────────
// The Command Center lives on a SEPARATE host (admin.unchartedtravel.agency).
// Host detection is NOT the security boundary — every /api/admin/* call is
// authorized server-side by security_middleware.py. Host detection only
// decides which UI surface renders and keeps admin links off the public site.
const _HOST = typeof window !== 'undefined' ? window.location.hostname : '';
const ADMIN_HOST = _HOST.startsWith('admin.');
// Surfaces where admin navigation is allowed to appear (admin subdomain + dev/preview).
const IS_ADMIN_SURFACE = (
  ADMIN_HOST ||
  _HOST.includes('preview.emergentagent.com') ||
  _HOST === 'localhost' ||
  _HOST === '127.0.0.1'
);

// Main App Component - Similar to MaterialApp in Flutter
function App() {
  // Device detection — single source of truth
  const device = useDevice();
  // i18n
  const { t, language, changeLanguage, languages } = useI18n();

  // State management - Similar to StatefulWidget state in Flutter
  const [currentView, setCurrentView] = useState(ADMIN_HOST ? 'command-center' : 'welcome'); // welcome, chat, settings, favorites, trips, contact
  const [messages, setMessages] = useState([]); // Similar to List<Message> in Flutter
  const [inputMessage, setInputMessage] = useState(''); // Similar to TextEditingController
  const [sessionId, setSessionId] = useState(null); // Session management
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [showPreferenceForm, setShowPreferenceForm] = useState(false); // Form visibility
  const [itinerary, setItinerary] = useState(null); // Current itinerary
  const [currentTheme, setCurrentTheme] = useState(() => {
    return localStorage.getItem('app_theme') || 'dark';
  }); // Theme management
  const [currentUser, setCurrentUser] = useState(null); // Updated for authentication
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem('auth_token'));
  const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
  const [showAddFavorite, setShowAddFavorite] = useState(false);
  const [userSettings, setUserSettings] = useState(null);
  const [personalData, setPersonalData] = useState(null);
  const [passportData, setPassportData] = useState(null);
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem('favorites');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [currentTrips, setCurrentTrips] = useState([]);
  const [featuredTrips, setFeaturedTrips] = useState([]);
  // Enhanced Featured Page States
  const [showTripDetails, setShowTripDetails] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showBookingFlow, setShowBookingFlow] = useState(false);
  const [showUserAccount, setShowUserAccount] = useState(false);
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  const [signupForm, setSignupForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  });

  const [authError, setAuthError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // Speech-to-Text State
  const [isRecording, setIsRecording] = useState(false);
  const [speechProviders, setSpeechProviders] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);

  // Booking Flow State
  const [currentBookingStep, setCurrentBookingStep] = useState('home'); // home, search, results, cart, payment, confirmation
  const [searchType, setSearchType] = useState('flights');
  const [searchParams, setSearchParams] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [bookingConfirmation, setBookingConfirmation] = useState(null);

  // State preservation and scroll memory
  const [savedState, setSavedState] = useState(null);
  const [showPopularDestinations, setShowPopularDestinations] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [showSupportCenter, setShowSupportCenter] = useState(false);
  const [activeWorkflowId, setActiveWorkflowId] = useState(null);

  // Mobile UI state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileActivityOpen, setMobileActivityOpen] = useState(false);

  // Pipeline progress state — live step tracking via WebSocket
  const [pipelineSteps, setPipelineSteps] = useState([]);
  useEffect(() => {
    const unsub = wsService.on('pipeline_progress', (data) => {
      if (data.status === 'active') {
        setPipelineSteps(prev => {
          const exists = prev.find(s => s.step === data.step);
          if (exists) return prev.map(s => s.step === data.step ? { ...s, status: 'active', detail: data.detail } : s);
          return [...prev, { step: data.step, status: 'active', detail: data.detail || '' }];
        });
      } else if (data.status === 'done') {
        setPipelineSteps(prev => prev.map(s => s.step === data.step ? { ...s, status: 'done' } : s));
      }
    });
    return () => unsub();
  }, []);

  // Feature toggles — default OFF to prevent flash of unwanted content
  const [manualSearchEnabled, setManualSearchEnabled] = useState(false);
  useEffect(() => {
    fetch(`${API}/feature-toggles`)
      .then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(d => { if (d && 'manual_search_enabled' in d) setManualSearchEnabled(d.manual_search_enabled === true); })
      .catch(() => {});
  }, []);

  // Toast notification state (#4)
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'info', duration = 4000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  };

  // Session expiry check (#31)
  const [sessionWarning, setSessionWarning] = useState(null);
  useEffect(() => {
    if (!authToken) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API}/session/status`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        const data = await res.json();
        if (data.expired) {
          setSessionWarning('expired');
          showToast('Session expired. Please log in again.', 'error', 8000);
          logout();
        } else if (data.expiring_soon) {
          setSessionWarning('expiring');
          showToast(`Session expires in ${data.minutes_remaining} minutes`, 'warning', 6000);
        }
      } catch (e) { /* silent */ }
    }, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [authToken]);

  // Handle destination card interactions
  const handleDestinationClick = (destination, isImageClick = false) => {
    if (isImageClick) {
      setCurrentView('trip-details');
      setSelectedDestination(destination);
      setShowPopularDestinations(false);
    } else {
      setShowPopularDestinations(false);
      setCurrentView('chat');
      setTimeout(() => {
        sendMessage(`I'm interested in visiting ${destination.name}. Can you help me plan a trip there?`);
      }, 500);
    }
  };

  // Onboarding (#20)
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  useEffect(() => {
    const seen = localStorage.getItem('onboarding_seen');
    if (!seen) setShowOnboarding(true);
  }, []);
  const dismissOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem('onboarding_seen', 'true');
  };

  // Notification preferences (#22)
  const [notifPrefs, setNotifPrefs] = useState(() => {
    const saved = localStorage.getItem('notif_prefs');
    return saved ? JSON.parse(saved) : { bookings: true, deals: true, flightStatus: true, security: true };
  });
  const updateNotifPref = (key, val) => {
    const updated = { ...notifPrefs, [key]: val };
    setNotifPrefs(updated);
    localStorage.setItem('notif_prefs', JSON.stringify(updated));
  };

  // Search persistence (#21)
  useEffect(() => {
    if (searchResults.length > 0) {
      sessionStorage.setItem('search_results', JSON.stringify(searchResults));
      sessionStorage.setItem('search_params', JSON.stringify(searchParams));
    }
  }, [searchResults, searchParams]);

  useEffect(() => {
    const saved = sessionStorage.getItem('search_results');
    if (saved) {
      try { setSearchResults(JSON.parse(saved)); } catch (e) { /* ignore */ }
    }
  }, []);

  // Save state before authentication
  const saveCurrentState = () => {
    const state = {
      currentView,
      currentBookingStep,
      searchParams,
      cartItems,
      showPopularDestinations,
    };
    setSavedState(state);
    localStorage.setItem('preAuthState', JSON.stringify(state));
  };

  // Restore state after authentication
  const restoreState = () => {
    const stored = localStorage.getItem('preAuthState');
    if (stored) {
      const state = JSON.parse(stored);
      setCurrentView(state.currentView);
      setCurrentBookingStep(state.currentBookingStep);
      setSearchParams(state.searchParams || {});
      setCartItems(state.cartItems || []);
      setShowPopularDestinations(state.showPopularDestinations || false);
      localStorage.removeItem('preAuthState');
    }
  };

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    inquiry_type: 'general'
  });
  const [travelPreferences, setTravelPreferences] = useState({
    destination: '',
    dates: '',
    budget: '',
    travelers: 1,
    interests: [],
    accommodation_type: ''
  });

  // Refs for scrolling - Similar to ScrollController in Flutter
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const chatMessagesRef = useRef(null);

  // Auto-scroll to bottom - Similar to animateTo in Flutter ScrollController
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  // Toggle theme - Similar to changing theme in Flutter ThemeData
  const toggleTheme = () => {
    const themes = ['light', 'dark', 'brand', 'explorer'];
    const currentIndex = themes.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themes.length;
    const next = themes[nextIndex];
    setCurrentTheme(next);
    localStorage.setItem('app_theme', next);
  };

  // Apply theme to body - Similar to Theme.of(context) in Flutter
  useEffect(() => {
    document.body.className = `theme-${currentTheme}`;
    localStorage.setItem('app_theme', currentTheme);
  }, [currentTheme]);

  // Load featured trips on startup
  useEffect(() => {
    loadFeaturedTrips();
    loadUserData();
    // Register service worker for offline support + push notifications
    notificationService.init();
    // Connect WebSocket for real-time updates
    wsService.connect(currentUser?.user_id || currentUser?.id || 'anonymous');
    // Listen for real-time notifications
    const unsub = wsService.on('notification', (data) => {
      if (data.data) notificationService.notify(data.data);
    });
    return () => { unsub(); wsService.disconnect(); };
  }, []);

  // Reconnect WebSocket when user logs in/out (user_id changes)
  useEffect(() => {
    if (currentUser?.user_id || currentUser?.id) {
      wsService.disconnect();
      wsService.connect(currentUser.user_id || currentUser.id);
    }
  }, [currentUser?.user_id, currentUser?.id]);

  // Install frontend logging once
  useEffect(() => {
    try { installFrontendLogging(); } catch (e) { console.warn('Logging init failed', e); }
  }, []);

  // Install responsive layout once
  useEffect(() => {
    try { installResponsiveLayout(); } catch (e) { console.warn('Responsive layout init failed', e); }
  }, []);

  useEffect(() => {
    setTimeout(scrollToBottom, 100);
  }, [messages]);

  // Data loading functions
  const loadFeaturedTrips = async () => {
    try {
      const response = await axios.get(`${API}/featured-trips`);
      setFeaturedTrips(response.data);
    } catch (error) {
      console.error('Error loading featured trips:', error);
      // Enhanced featured trips with detailed activities for carousel
      setFeaturedTrips([
        {
          id: '1',
          title: 'Magical Paris Adventure',
          description: 'Experience the City of Light with iconic landmarks, world-class museums, and romantic Seine cruises.',
          destination: 'Paris, France',
          featured_image: 'https://images.pexels.com/photos/587838/pexels-photo-587838.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop',
          price_from: 1299,
          duration_days: 5,
          highlights: ['Eiffel Tower at sunset', 'Louvre private tour', 'Seine river cruise', 'Montmartre exploration'],
          activities: [
            { name: 'Eiffel Tower Skip-the-Line', description: 'Priority access with champagne at summit', duration: '2 hours' },
            { name: 'Louvre Private Tour', description: 'Expert guide through masterpieces', duration: '3 hours' },
            { name: 'Seine Evening Cruise', description: 'Romantic dinner cruise with city lights', duration: '2.5 hours' },
            { name: 'Montmartre Art Walk', description: 'Discover artists\' quarter and Sacré-Cœur', duration: '3 hours' }
          ]
        },
        {
          id: '2',
          title: 'Tokyo Urban Explorer',
          description: 'Dive into Japan\'s vibrant capital blending ancient traditions with cutting-edge modernity.',
          destination: 'Tokyo, Japan',
          featured_image: 'https://images.pexels.com/photos/2816904/pexels-photo-2816904.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop',
          price_from: 1899,
          duration_days: 7,
          highlights: ['Shibuya crossing experience', 'Mount Fuji day trip', 'Traditional tea ceremony', 'Robot restaurant show'],
          activities: [
            { name: 'Shibuya Crossing & Harajuku', description: 'Experience the world\'s busiest intersection', duration: '4 hours' },
            { name: 'Mount Fuji & Lake Kawaguchi', description: 'Full day trip with scenic views and lunch', duration: '10 hours' },
            { name: 'Traditional Tea Ceremony', description: 'Authentic ceremony in historic tea house', duration: '2 hours' },
            { name: 'Tsukiji Fish Market Tour', description: 'Early morning market visit with sushi breakfast', duration: '3 hours' }
          ]
        },
        {
          id: '3',
          title: 'Santorini Sunset Escape',
          description: 'Unwind in Greece\'s most romantic island with stunning sunsets and pristine beaches.',
          destination: 'Santorini, Greece',
          featured_image: 'https://images.unsplash.com/photo-1633909198480-85595aa21285?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxTYW50b3JpbmklMjBHcmVlY2UlMjB3aGl0ZSUyMGJ1aWxkaW5ncyUyMG9jZWFufGVufDB8fHx8MTc3NDM3MDIxN3ww&ixlib=rb-4.1.0&q=85&w=1920&fit=max',
          price_from: 999,
          duration_days: 4,
          highlights: ['Oia sunset viewing', 'Wine tasting tour', 'Volcano boat trip', 'Traditional Greek cooking class'],
          activities: [
            { name: 'Oia Sunset Experience', description: 'Premium viewing spot with local wine', duration: '3 hours' },
            { name: 'Santorini Wine Tour', description: 'Visit 3 wineries with traditional mezze', duration: '6 hours' },
            { name: 'Volcano & Hot Springs', description: 'Boat trip to volcanic islands with swimming', duration: '8 hours' },
            { name: 'Greek Cooking Class', description: 'Learn to cook traditional dishes with locals', duration: '4 hours' }
          ]
        },
        {
          id: '4',
          title: 'Bali Spiritual Journey',
          description: 'Find inner peace in the Island of Gods with temple visits, yoga retreats, and pristine beaches.',
          destination: 'Bali, Indonesia',
          featured_image: 'https://images.pexels.com/photos/17716409/pexels-photo-17716409.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop',
          price_from: 799,
          duration_days: 6,
          highlights: ['Sunrise at Mount Batur', 'Ubud monkey forest', 'Traditional spa treatment', 'Tanah Lot temple'],
          activities: [
            { name: 'Mount Batur Sunrise Trek', description: 'Early morning hike with breakfast at summit', duration: '6 hours' },
            { name: 'Ubud Cultural Tour', description: 'Rice terraces, monkey forest, and art villages', duration: '8 hours' },
            { name: 'Traditional Balinese Spa', description: 'Full body massage and flower bath treatment', duration: '3 hours' },
            { name: 'Temple Hopping Tour', description: 'Visit Tanah Lot, Uluwatu, and Besakih temples', duration: '10 hours' }
          ]
        },
        {
          id: '5',
          title: 'New York City Lights',
          description: 'Experience the city that never sleeps with Broadway shows, world-class museums, and iconic landmarks.',
          destination: 'New York, USA',
          featured_image: 'https://images.pexels.com/photos/722241/pexels-photo-722241.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop',
          price_from: 1199,
          duration_days: 4,
          highlights: ['Statue of Liberty cruise', 'Broadway show tickets', 'Central Park bike tour', 'Top of the Rock sunset'],
          activities: [
            { name: 'Statue of Liberty & Ellis Island', description: 'Ferry cruise with audio guide tour', duration: '4 hours' },
            { name: 'Broadway Show Experience', description: 'Premium seats to hit musical with dinner', duration: '5 hours' },
            { name: 'Central Park Bike Tour', description: 'Guided tour through Manhattan\'s green heart', duration: '3 hours' },
            { name: 'Empire State & Top of the Rock', description: 'Visit both iconic observatories', duration: '4 hours' }
          ]
        }
      ]);
    }
  };

  // Authentication functions
  const checkAuthForProtectedRoute = (route) => {
    if (['favorites', 'trips', 'settings'].includes(route) && !isAuthenticated) {
      // Show login prompt for protected routes
      showToast('Please log in to access this feature.', 'warning');
      setCurrentView('login');
      return false;
    }
    return true;
  };

  const handleNavigation = (route) => {
    if (checkAuthForProtectedRoute(route)) {
      setCurrentView(route);
      // Clear any booking state when navigating away from search flow
      if (!['search', 'results', 'cart', 'payment', 'confirmation'].includes(route)) {
        setCurrentBookingStep('home');
      }
    }
  };

  // Navigate to home function
  const navigateToHome = () => {
    setCurrentView('welcome');
    setCurrentBookingStep('home');
    setShowPopularDestinations(false);
  };

  // Load user data on authentication
  const loadUserData = async () => {
    try {
      if (isAuthenticated && authToken) {
        // Load detailed bookings
        const headers = { Authorization: `Bearer ${authToken}` };
        const bookingsResponse = await axios.get(`${API}/user/${currentUser.id}/bookings-detailed`, { headers });
        setCurrentTrips(bookingsResponse.data);
      } else {
        // Not logged in — show empty state
        setFavorites([]);
        setCurrentTrips([]);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setCurrentTrips([]);
      setFavorites([]);
    }
  };

  // Generate session ID - Similar to UUID generation in Flutter
  const generateSessionId = () => {
    return 'session_' + Math.random().toString(36).substr(2, 9);
  };

  // Send message to Lauren - Similar to calling API service in Flutter
  const sendMessage = async (messageText = null) => {
    const messageToSend = messageText || inputMessage;
    if (!messageToSend.trim()) return;

    setIsLoading(true);
    setPipelineSteps([]);
    const sendStartTime = performance.now();
    
    // Add user message to chat
    const userMessage = {
      id: Date.now(),
      message: messageToSend,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');

    try {
      // Create session if needed
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        currentSessionId = generateSessionId();
        setSessionId(currentSessionId);
      }

      // Enhanced Lauren AI - Handle special requests proactively
      let enhancedMessage = messageToSend;
      
      // Detect deal requests and provide immediate response
      if (messageToSend.toLowerCase().includes('deal') || 
          messageToSend.toLowerCase().includes('discount') ||
          messageToSend.toLowerCase().includes('special offer') ||
          messageToSend.toLowerCase().includes('cheap') ||
          messageToSend.toLowerCase().includes('budget')) {
        // Let Lauren handle deal requests naturally via the AI pipeline
        // No hardcoded deals — all data comes from live APIs
      }

      // Call chat API with proper user identification
      const chatPayload = {
        message: enhancedMessage,
        session_id: currentSessionId,
        user_id: currentUser?.id || 'guest_user'
      };

      // Add authentication header if user is logged in
      const headers = {};
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      const response = await axios.post(`${API}/chat`, chatPayload, { headers, timeout: 50000 });
      const responseTimeMs = Math.round(performance.now() - sendStartTime);

      // Track workflow if present
      if (response.data.workflow_id) {
        setActiveWorkflowId(response.data.workflow_id);
      }

      // Determine response speed label
      const pipelineUsed = response.data.data?.pipeline_steps?.length > 0;
      let speedLabel;
      if (responseTimeMs < 500) speedLabel = 'instant';
      else if (responseTimeMs < 3000) speedLabel = 'fast';
      else if (responseTimeMs < 10000) speedLabel = 'researched';
      else speedLabel = 'deep';

      // Add Lauren's response
      const laurenMessage = {
        id: Date.now() + 1,
        message: response.data.message,
        sender: 'lauren',
        timestamp: new Date(),
        message_type: response.data.message_type,
        data: response.data.data,
        workflow_id: response.data.workflow_id,
        responseTimeMs,
        speedLabel,
        pipelineUsed,
      };
      
      setMessages(prev => [...prev, laurenMessage]);

      // Check if we should show preference form
      if (response.data.message_type === 'preference_form') {
        setShowPreferenceForm(true);
      }

    } catch (error) {
      console.error('Chat error:', error);
      // Add error message
      const errorMessage = {
        id: Date.now() + 1,
        message: "Sorry, I'm having trouble connecting right now. Please try again! 🌍",
        sender: 'lauren',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Interest options for preferences
  const interestOptions = [
    'adventure', 'culture', 'food', 'nightlife', 'relaxation', 'nature', 'history', 'art'
  ];

  // Toggle interest selection
  const toggleInterest = (interest) => {
    setTravelPreferences(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  // Save travel preferences
  const saveTravelPreferences = async () => {
    if (!sessionId) return;

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/preferences/${sessionId}`, travelPreferences);
      
      const laurenMessage = {
        id: Date.now(),
        message: response.data.message,
        sender: 'lauren',
        timestamp: new Date(),
        message_type: 'itinerary'
      };
      setMessages(prev => [...prev, laurenMessage]);
      
      setItinerary(response.data.itinerary);
      setShowPreferenceForm(false);
      setCurrentView('itinerary');

    } catch (error) {
      console.error('Preferences error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Guest login function — disabled (all users must register)
  /* const guestLogin = async () => {
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const response = await axios.post(`${API}/auth/guest`);
      
      const { access_token, user } = response.data;
      
      // Store authentication data
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('current_user', JSON.stringify(user));
      
      setAuthToken(access_token);
      setCurrentUser(user);
      setIsAuthenticated(true);
      
      // Redirect to welcome page
      setCurrentView('welcome');
      
    } catch (error) {
      console.error('Guest login error:', error);
      setAuthError(error.response?.data?.detail || 'Guest login failed. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }; */

  // Authentication API functions
  const login = async (credentials) => {
    setAuthLoading(true);
    setAuthError('');
    
    // Debug logging
    
    // Save current state before login
    saveCurrentState();
    
    try {
      const formData = new FormData();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);
      
      const response = await axios.post(`${API}/auth/login`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      const { access_token, user } = response.data;
      
      // Store authentication data
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('current_user', JSON.stringify(user));
      
      setAuthToken(access_token);
      setCurrentUser(user);
      setIsAuthenticated(true);
      
      // Load user data
      await loadUserData();
      
      // Check if user is admin and redirect to admin panel
      if (user.role === 'admin' || user.role === 'dev' || user.role === 'customer_service') {
        setCurrentView('admin');
      } else {
        // Restore previous state for regular users
        setTimeout(() => {
          restoreState();
        }, 100);
        
        // Redirect to welcome page
        setCurrentView('welcome');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const signup = async (userData) => {
    setAuthLoading(true);
    setAuthError('');
    
    if (userData.password !== userData.confirmPassword) {
      setAuthError('Passwords do not match');
      setAuthLoading(false);
      return;
    }
    
    // Save current state before signup
    saveCurrentState();
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        username: userData.username,
        email: userData.email,
        password: userData.password,
        full_name: userData.fullName
      });
      
      const { access_token, user } = response.data;
      
      // Store authentication data
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('current_user', JSON.stringify(user));
      
      setAuthToken(access_token);
      setCurrentUser(user);
      setIsAuthenticated(true);
      setSignupSuccess(true);
      setAuthError('');
      
      // Load user data
      await loadUserData();
      
      // Check if user is admin and redirect to admin panel
      if (user.role === 'admin' || user.role === 'dev' || user.role === 'customer_service') {
        setTimeout(() => {
          setSignupSuccess(false);
          setCurrentView('admin');
        }, 1800);
      } else {
        // Show success briefly, then redirect to chat with Lauren
        setTimeout(() => {
          setSignupSuccess(false);
          setCurrentView('chat');
        }, 1800);
      }
      
    } catch (error) {
      console.error('Signup error:', error);
      setAuthError(error.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    
    setAuthToken(null);
    setCurrentUser(null);
    setIsAuthenticated(false);
    
    // Clear user data
    setUserSettings(null);
    setPersonalData(null);
    setPassportData(null);
    setFavorites([]);
    setCurrentTrips([]);
    
    setCurrentView('welcome');
  };

  // createGuestSession — disabled (all users must register)
  /* const createGuestSession = async () => {
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const response = await axios.post(`${API}/auth/guest`);
      const { access_token, user } = response.data;
      
      // Store guest session data
      sessionStorage.setItem('guest_token', access_token);
      sessionStorage.setItem('guest_user', JSON.stringify(user));
      
      setAuthToken(access_token);
      setCurrentUser(user);
      setIsAuthenticated(true);
      
      setCurrentView('welcome');
      
    } catch (error) {
      console.error('Guest session error:', error);
      setAuthError(error.response?.data?.detail || 'Guest access is currently unavailable.');
    } finally {
      setAuthLoading(false);
    }
  }; */

  // Check for existing authentication on app load
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const user = localStorage.getItem('current_user');
    
    if (token && user) {
      setAuthToken(token);
      setCurrentUser(JSON.parse(user));
      setIsAuthenticated(true);
      loadUserData();
    }
    
    // Load speech providers
    loadSpeechProviders();

    // Handle Stripe redirect URLs (/success?session_id=... or /cancel)
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    if (path === '/success' && params.get('session_id')) {
      setCurrentView('payment-success');
    } else if (path === '/cancel') {
      setCurrentView('payment-cancel');
    }
  }, []);

  // Speech-to-Text Functions
  const loadSpeechProviders = async () => {
    try {
      const response = await axios.get(`${API}/speech/providers`);
      setSpeechProviders(response.data);
      
      // Set default provider based on availability
      const providers = response.data.providers;
      if (providers.openai?.available) {
        setSelectedProvider('openai');
      } else if (providers.deepgram?.available) {
        setSelectedProvider('deepgram');
      } else {
        setSelectedProvider('openai'); // Default fallback
      }
    } catch (error) {
      console.error('Error loading speech providers:', error);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      const chunks = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        
        // Clean up
        stream.getTracks().forEach(track => track.stop());
      };
      
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setIsRecording(true);
      recorder.start();
      
    } catch (error) {
      console.error('Error starting recording:', error);
      showToast('Could not access microphone. Please check permissions.', 'error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.webm');
      formData.append('provider', selectedProvider);
      formData.append('language', 'en');
      
      const headers = {};
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }
      
      const response = await axios.post(`${API}/speech/transcribe`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const transcription = response.data;
      
      if (transcription.text && transcription.text.length > 10) {
        // Set the transcribed text as input message
        setInputMessage(transcription.text);
        
        // Show success notification
        showNotification(`🎤 Transcribed (${transcription.provider}): ${transcription.confidence ? (transcription.confidence * 100).toFixed(0) + '%' : ''} confidence`, 'success');
      } else {
        showNotification('🔄 Please try recording again or speak more clearly', 'warning');
      }
      
    } catch (error) {
      console.error('Transcription error:', error);
      showNotification('❌ Transcription failed. Please try again or type your message.', 'error');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Booking Flow Functions
  const handleSearch = async (type, params) => {
    setSearchType(type);
    setSearchParams(params);
    setCurrentBookingStep('results');
    
    // Perform actual search API call
    try {
      setIsLoading(true);
      
      // Add authentication header if available
      const headers = {};
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }
      
      let response;
      
      // Use appropriate endpoints based on search type
      switch (type) {
        case 'flights':
          // Use POST endpoint for flights
          response = await axios.post(`${API}/travel/flights/search`, params, { headers });
          break;
          
        case 'hotels':
          // Use GET endpoint for hotels with query parameters
          const hotelQuery = new URLSearchParams({
            city_code: params.city_code || params.destination || 'NYC',
            check_in_date: params.check_in_date || params.departureDate,
            check_out_date: params.check_out_date || params.returnDate,
            adults: params.adults || params.travelers || 1,
            rooms: params.rooms || 1
          });
          response = await axios.get(`${API}/travel/hotels/search?${hotelQuery}`, { headers });
          break;
          
        case 'cars':
          // Use POST endpoint for car rentals with Amadeus
          response = await axios.post(`${API}/amadeus/car-rentals/search`, {
            pickup_location: params.pickup_location || params.destination || 'NYC',
            pickup_date: params.pickup_date || params.departureDate,
            dropoff_date: params.dropoff_date || params.returnDate
          }, { headers });
          break;
          
        case 'activities':
          // Use GET endpoint for activities
          const activityQuery = new URLSearchParams({
            latitude: params.latitude || 40.7128, // Default to NYC coordinates
            longitude: params.longitude || -74.0060,
            radius: params.radius || 50
          });
          response = await axios.get(`${API}/travel/activities/search?${activityQuery}`, { headers });
          break;
          
        default:
          // Default to flights
          response = await axios.post(`${API}/travel/flights/search`, params, { headers });
      }
      
      // Handle different response structures
      let searchData = [];
      if (response.data.success !== false) {
        // Extract the actual data from different response formats
        if (type === 'flights') {
          searchData = response.data.offers || response.data.flights || response.data;
        } else if (type === 'hotels') {
          searchData = response.data.hotels || response.data.data || response.data;
        } else if (type === 'cars') {
          // For Amadeus car rentals, data might be in .data.car_rentals or .data
          searchData = response.data.data?.car_rentals || response.data.car_rentals || response.data.data || response.data;
        } else if (type === 'activities') {
          searchData = response.data.activities || response.data.data || response.data;
        } else {
          searchData = response.data;
        }
      }
      
      // Update search results
      setSearchResults(Array.isArray(searchData) ? searchData : []);
      
      const resultCount = Array.isArray(searchData) ? searchData.length : 0;
      if (resultCount > 0) {
        showNotification(`✅ Found ${resultCount} ${type} options!`, 'success');
      } else {
        showNotification(`❌ No ${type} found for your search criteria. Try different dates or locations.`, 'warning');
      }
      
    } catch (error) {
      console.error('Search error:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || error.message || 'Search failed';
      showNotification(`❌ Search failed: ${errorMessage}`, 'error');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookItem = (type, item) => {
    const bookingItem = { ...item, type };
    setCartItems(prev => [...prev, bookingItem]);
    showNotification(`${item.name || item.airline || item.title} added to your trip!`, 'success');
    setCurrentView('welcome');
    setCurrentBookingStep('cart');
  };

  const handleRemoveFromCart = (index) => {
    setCartItems(prev => prev.filter((_, i) => i !== index));
    showNotification('Item removed from cart', 'info');
  };

  const handleProceedToPayment = (totalAmount, hasInsurance) => {
    setCurrentBookingStep('payment');
  };

  const handlePaymentSuccess = (confirmation) => {
    setBookingConfirmation(confirmation);
    setCurrentBookingStep('confirmation');
    setCartItems([]); // Clear cart
  };

  const handleBackToHome = () => {
    setCurrentBookingStep('home');
    setCurrentView('home');
  };

  const showNotification = (message, type = 'info') => {
    // Simple notification system - could be enhanced with a proper toast library
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      background: ${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : type === 'warning' ? '#f59e0b' : '#3b82f6'};
      color: white;
      border-radius: 8px;
      z-index: 1000;
      font-size: 14px;
      max-width: 300px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 4000);
  };

  // Carousel navigation functions
  const nextFeatured = () => {
    setCurrentFeaturedIndex((prevIndex) => 
      prevIndex === featuredTrips.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevFeatured = () => {
    setCurrentFeaturedIndex((prevIndex) => 
      prevIndex === 0 ? featuredTrips.length - 1 : prevIndex - 1
    );
  };

  // Enhanced Featured Trip Handlers
  const handleFeaturedTripClick = (trip) => {
    if (!trip) {
      console.error("No trip data provided to handleFeaturedTripClick");
      return;
    }
    setSelectedTrip(trip);
    setShowTripDetails(true);
  };

  const handleBookTrip = (trip) => {
    // Add the featured trip to cart and navigate to cart
    const item = {
      type: 'package',
      title: trip?.title || trip?.name || 'Selected Trip',
      name: trip?.title || trip?.name || 'Selected Trip',
      price: trip?.price_from || trip?.price || 0,
      location: trip?.destination,
    };
    setCartItems(prev => [...prev, item]);
    setCurrentBookingStep('cart');
    setCurrentView('welcome');
    setShowTripDetails(false);
  };

  const closeTripDetails = () => {
    setShowTripDetails(false);
    setSelectedTrip(null);
  };

  const closeBookingFlow = () => {
    setShowBookingFlow(false);
    setSelectedTrip(null);
  };

  // Auto-rotate featured destinations every 8 seconds
  useEffect(() => {
    // Only run the carousel when the user is on the home/welcome screen
    if (featuredTrips.length > 0 && currentView === 'welcome' && currentBookingStep === 'home') {
      const interval = setInterval(nextFeatured, 8000);
      return () => clearInterval(interval);
    }
  }, [featuredTrips.length, currentView, currentBookingStep]);

  // Submit contact form
  const submitContactForm = async () => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      showToast('Thank you! We will get back to you within 24 hours.', 'success');
      setContactForm({
        name: '',
        email: '',
        subject: '',
        message: '',
        inquiry_type: 'general'
      });
    } catch (error) {
      console.error('Contact form error:', error);
      showToast('Error sending message. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Dynamic featured section updates (24-48hrs for logged in, weekly for guests)
  useEffect(() => {
    const updateInterval = isAuthenticated ? 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000; // 24 hours or 7 days
    const lastUpdate = localStorage.getItem('lastFeaturedUpdate');
    const now = Date.now();
    
    if (!lastUpdate || now - parseInt(lastUpdate) > updateInterval) {
      loadFeaturedTrips();
      localStorage.setItem('lastFeaturedUpdate', now.toString());
    }
  }, [isAuthenticated]);

  // ═══ Mobile Bottom Navigation ═══
  const MobileBottomNav = () => {
    if (!device.isMobile) return null;
    return (
      <div className="mobile-bottom-nav" data-testid="mobile-bottom-nav">
        <button className={`mob-nav-item ${currentView === 'welcome' && currentBookingStep === 'home' ? 'active' : ''}`}
          onClick={() => { setCurrentView('welcome'); setCurrentBookingStep('home'); }}>
          <span className="mob-nav-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></span>
          <span>{t('home', 'Home')}</span>
        </button>
        {manualSearchEnabled && (
        <button className={`mob-nav-item ${currentBookingStep === 'search' ? 'active' : ''}`}
          onClick={() => { setCurrentView('welcome'); setCurrentBookingStep('search'); }}>
          <span className="mob-nav-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
          <span>{t('search', 'Search')}</span>
        </button>
        )}
        <button className={`mob-nav-item ${currentView === 'chat' ? 'active' : ''}`}
          onClick={() => { setCurrentView('chat'); setCurrentBookingStep('home'); }}>
          <span className="mob-nav-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
          <span>{t('chat', 'Chat')}</span>
        </button>
        {isAuthenticated && (
          <button className={`mob-nav-item ${currentView === 'trips' ? 'active' : ''}`}
            onClick={() => handleNavigation('trips')}>
            <span className="mob-nav-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg></span>
            <span>Trips</span>
          </button>
        )}
        <button className="mob-nav-item"
          onClick={() => setMobileMenuOpen(true)}>
          <span className="mob-nav-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg></span>
          <span>More</span>
        </button>
      </div>
    );
  };

  // ═══ Mobile Slide-in Menu ═══
  const MobileMenuOverlay = () => {
    if (!device.isMobile || !mobileMenuOpen) return null;
    const close = () => setMobileMenuOpen(false);
    const go = (view, step) => { close(); if (view) setCurrentView(view); if (step) setCurrentBookingStep(step); };

    return (
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={close}>
        <div className="mobile-menu-panel" onClick={e => e.stopPropagation()}>
          <button className="mobile-menu-close" onClick={close}>&times;</button>
          
          {isAuthenticated ? (
            <div style={{padding: '0 1rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem'}}>
              <p style={{color: '#d4a054', fontWeight: 600, margin: 0}}>{currentUser?.username}</p>
              <p style={{color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', margin: '2px 0 0'}}>{currentUser?.role}</p>
            </div>
          ) : (
            <button className="mob-menu-item" onClick={() => go('login')} style={{color: '#d4a054', marginBottom: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '1rem'}}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              Sign In / Create Account
            </button>
          )}

          <button className="mob-menu-item" onClick={() => go('favorites')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            Favorites
          </button>
          <button className="mob-menu-item" onClick={() => go('welcome', 'cart')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
            Cart {cartItems.length > 0 && `(${cartItems.length})`}
          </button>
          {isAuthenticated && (
          <button className="mob-menu-item" onClick={() => go('settings')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Settings
          </button>
          )}
          {isAuthenticated && (
          <button className="mob-menu-item" onClick={() => { close(); setCurrentView('notification-settings'); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            Notification Settings
          </button>
          )}
          <button className="mob-menu-item" onClick={() => { close(); setShowSupportCenter(true); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Support
          </button>

          {isAuthenticated && ['admin', 'dev', 'customer_service'].includes(currentUser?.role) && (
            <button className="mob-menu-item" onClick={() => go('admin')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              Admin Dashboard
            </button>
          )}

          {isAuthenticated && IS_ADMIN_SURFACE && ['admin', 'dev'].includes(currentUser?.role) && (
            <button className="mob-menu-item" onClick={() => go('command-center')} data-testid="mob-command-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
              Command Center
            </button>
          )}

          <div style={{flex: 1}} />

          {isAuthenticated && (
            <button className="mob-menu-item" onClick={() => { close(); logout(); }} style={{
              color: '#f87171', marginTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '1rem',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Sign Out ({currentUser?.username})
            </button>
          )}
        </div>
      </div>
    );
  };

  // Main Navigation Component - Enhanced for UX refinements
  const MainNavigation = () => (
    <nav className="main-navigation luxury-nav">
      <div className="nav-container">
        {/* Premium Logo Section - Left Edge */}
        <div className="nav-brand-left-edge">
          <div className="brand-logo-minimal" onClick={navigateToHome}>
            <div className="brand-text-left">
              <h1 className="brand-golden-minimal">Uncharted</h1>
              <span className="brand-subtitle-minimal">Travels</span>
            </div>
          </div>
        </div>
        
        {/* Center Navigation — hidden on mobile (replaced by bottom nav) */}
        {!device.isMobile && (
        <div className="nav-center">
          <div className="nav-menu-horizontal">
            <button 
              className={`nav-item-horizontal ${(currentBookingStep === 'home' && currentView === 'welcome') ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('welcome');
                setCurrentBookingStep('home');
              }}
            >
              <span className="nav-icon-horizontal">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </span>
              <span className="nav-text-horizontal">{t('home', 'Home')}</span>
            </button>

            {manualSearchEnabled && (
            <button 
              className={`nav-item-horizontal ${currentBookingStep === 'search' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('welcome');
                setCurrentBookingStep('search');
              }}
            >
              <span className="nav-icon-horizontal">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <span className="nav-text-horizontal">{t('search', 'Search')}</span>
            </button>
            )}

            <button 
              className={`nav-item-horizontal chat-enhanced-golden ${currentView === 'chat' ? 'active' : ''}`}
              onClick={() => {
                setCurrentView('chat');
                setCurrentBookingStep('home');
              }}
            >
              <span className="nav-icon-horizontal">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </span>
              <span className="nav-text-horizontal">{t('chat', 'Chat')}</span>
            </button>

            {isAuthenticated && (
              <>
                <button 
                  className={`nav-item-horizontal ${currentBookingStep === 'cart' ? 'active' : ''} ${cartItems.length > 0 ? 'has-items' : ''}`}
                  onClick={() => {
                    setCurrentView('welcome');
                    setCurrentBookingStep('cart');
                  }}
                >
                  <span className="nav-icon-horizontal">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                  </span>
                  <span className="nav-text-horizontal">{t('cart', 'Cart')}</span>
                  {cartItems.length > 0 && (
                    <span className="cart-badge-horizontal">{cartItems.length}</span>
                  )}
                </button>
                
                <button 
                  className={`nav-item-horizontal ${currentView === 'favorites' ? 'active' : ''}`}
                  onClick={() => handleNavigation('favorites')}
                >
                  <span className="nav-icon-horizontal">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  </span>
                  <span className="nav-text-horizontal">{t('favorites', 'Favorites')}</span>
                </button>
                <button 
                  className={`nav-item-horizontal ${currentView === 'trips' ? 'active' : ''}`}
                  onClick={() => handleNavigation('trips')}
                >
                  <span className="nav-icon-horizontal">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                  </span>
                  <span className="nav-text-horizontal">{t('bookings', 'Bookings')}</span>
                </button>
                {isAuthenticated && (
                <button 
                  className={`nav-item-horizontal ${currentView === 'settings' ? 'active' : ''}`}
                  onClick={() => handleNavigation('settings')}
                >
                  <span className="nav-icon-horizontal">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </span>
                  <span className="nav-text-horizontal">{t('settings', 'Settings')}</span>
                </button>
                )}
                <button 
                  data-testid="support-nav-btn"
                  className="nav-item-horizontal"
                  onClick={() => setShowSupportCenter(true)}
                >
                  <span className="nav-icon-horizontal">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </span>
                  <span className="nav-text-horizontal">{t('support', 'Support')}</span>
                </button>
              </>
            )}
          </div>
        </div>
        )}

        {/* Right Actions */}
        <div className="nav-actions-right-edge">
          {isAuthenticated && (
            <NotificationBell userId={currentUser?.user_id} onViewAll={() => setCurrentView('notification-center')} />
          )}
          {isAuthenticated && ['admin', 'dev', 'customer_service'].includes(currentUser?.role) && (
            <button 
              className={`admin-access-btn ${currentView === 'admin' ? 'active' : ''}`}
              onClick={() => setCurrentView('admin')}
              title="Admin Dashboard"
            >
              <span className="admin-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              </span>
            </button>
          )}
          {isAuthenticated && IS_ADMIN_SURFACE && ['admin', 'dev'].includes(currentUser?.role) && (
            <button 
              className={`admin-access-btn ${currentView === 'command-center' ? 'active' : ''}`}
              onClick={() => setCurrentView('command-center')}
              title="Command Center"
              data-testid="nav-command-center"
              style={{ marginLeft: 2 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
            </button>
          )}
          
          {isAuthenticated ? (
            <div className="user-menu-premium">
              {!device.isMobile && (
                <div className="user-info">
                  <span className="user-name">{currentUser?.username}</span>
                  <span className="user-role">{currentUser?.role}</span>
                </div>
              )}
              <button className="logout-btn" onClick={logout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              </button>
            </div>
          ) : (
            <button 
              className="login-access-btn-premium"
              onClick={() => setCurrentView('login')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              {!device.isMobile && <span className="login-text">Login</span>}
            </button>
          )}
        </div>
      </div>
    </nav>
  );

  // Welcome Screen Component - Fixed Layout
  const WelcomeScreen = () => (
    <>
      {/* Fixed Featured Section Layout with Background Images */}
      <div className="hero-featured-section">
        <div className="hero-container">
          {featuredTrips.length > 0 && featuredTrips[currentFeaturedIndex] && (
            <div 
              key={`featured-card-${currentFeaturedIndex}`}
              className="hero-card-layout"
              style={{
                '--hero-bg-image': `url(${featuredTrips[currentFeaturedIndex].featured_image})`,
                cursor: 'pointer'
              }}
              onClick={(e) => {
                // Only handle the click if it's not from a button
                if (!e.target.closest('button')) {
                  handleFeaturedTripClick(featuredTrips[currentFeaturedIndex]);
                }
              }}
            >
              {/* Content Section - Now in foreground */}
              <div className="hero-content-section">
                {/* Top left - Experience type, name, and location */}
                <div className="hero-header-section">
                  <div className="hero-experience-type">✨ Experience</div>
                  <h1 className="hero-title">{featuredTrips[currentFeaturedIndex].title}</h1>
                  <div className="hero-location">
                    <span className="location-pin">📍</span>
                    <span>{featuredTrips[currentFeaturedIndex].destination}</span>
                  </div>
                </div>

                {/* Center - Description with light container */}
                <div className="hero-description">
                  <p>{featuredTrips[currentFeaturedIndex].description}</p>
                </div>

                {/* Bottom right - Price and Duration */}
                <div className="hero-details">
                  <div className="detail-tag price-tag">
                    <span className="tag-label">From</span>
                    <span className="tag-value">${featuredTrips[currentFeaturedIndex].price_from}</span>
                  </div>
                  <div className="detail-tag duration-tag">
                    <span className="tag-label">Duration</span>
                    <span className="tag-value">{featuredTrips[currentFeaturedIndex].duration_days} Days</span>
                  </div>
                    {/* Heart Icon for Featured Trip */}
                    <button 
                      key={`heart-${featuredTrips[currentFeaturedIndex]?.id || currentFeaturedIndex}`}
                      className={`favorite-heart-btn featured-heart ${isFavorite(featuredTrips[currentFeaturedIndex]) ? 'favorited' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        addToFavorites(featuredTrips[currentFeaturedIndex]);
                      }}
                      title={isFavorite(featuredTrips[currentFeaturedIndex]) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {isFavorite(featuredTrips[currentFeaturedIndex]) ? '❤️' : '🤍'}
                    </button>
                </div>

                {/* Bottom left - Enhanced Featured Experiences */}
                <div className="hero-activities" aria-label="Featured Experiences" role="complementary">
                  <h3 className="activities-heading">✨ Featured Experiences</h3>
                  <div className="activities-list">
                    {(featuredTrips[currentFeaturedIndex]?.activities || []).slice(0, 4).map((activity, index) => (
                      <div key={index} className="activity-item">
                        <h4 className="activity-name">{activity.name}</h4>
                        <p className="activity-description">{activity.description}</p>
                        <span className="activity-duration">⏱️ {activity.duration}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bottom center - Explore More button moved down */}
                <div className="hero-actions">
                  <button 
                    className="explore-destinations-btn"
                    onClick={() => setShowPopularDestinations(true)}
                    data-testid="explore-destinations-btn"
                  >
                    <span>🌍</span>
                    Explore More Destinations
                  </button>
                </div>

                {/* Lauren CTA: A Better Way to Search */}
                <section className="lauren-cta" aria-label="A Better Way to Search">
                  <div className="cta-copy">
                    <h3>✨ A Better Way to Search</h3>
                    <p>Tell Lauren what you want, and she’ll search, compare, pick seats, and handle baggage—end to end.</p>
                  </div>
                  <div className="cta-actions">
                    <button className="primary-cta-btn" onClick={() => { setCurrentView('chat'); setCurrentBookingStep('home'); }}>Chat with Lauren</button>
                    {manualSearchEnabled && (
                    <button className="secondary-cta-btn" onClick={() => { setCurrentView('welcome'); setCurrentBookingStep('search'); }}>Manual Search</button>
                    )}
                  </div>
                </section>
              </div>
              
              {/* Carousel Navigation */}
              <button data-testid="carousel-prev" className="carousel-nav-left" onClick={(e) => {
                e.stopPropagation();
                prevFeatured();
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button data-testid="carousel-next" className="carousel-nav-right" onClick={(e) => {
                e.stopPropagation();
                nextFeatured();
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          )}

          {/* Carousel Indicators */}
          <div className="hero-indicators">
            {featuredTrips.map((_, index) => (
              <button
                key={index}
                className={`hero-indicator ${index === currentFeaturedIndex ? 'active' : ''}`}
                onClick={() => setCurrentFeaturedIndex(index)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Popular Destinations Popup with Background Images */}
      {showPopularDestinations && (
        <div className="destinations-popup-overlay">
          <div className="destinations-popup-container">
            <div className="popup-header-enhanced">
              <h2 className="popup-title">Popular Destinations</h2>
              <button 
                className="popup-close-btn" 
                onClick={() => setShowPopularDestinations(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="destinations-popup-content">
              <div className="destinations-grid-enhanced">
              {[
                {
                  name: "Bali, Indonesia",
                  image: "https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w5fDB8MXxhbGx8fHx8fHx8fHwxNjk5NzE4ODcx&ixlib=rb-4.0.3&q=85&w=1600",
                  description: "FOMO alert! This viral paradise is breaking Instagram with hidden temple pools and secret sunrise spots everyone's obsessing over",
                  popularity: "#1 Trending",
                  price: "From $799",
                  duration: "7 days",
                  details: {
                    duration: "7 days",
                    highlights: ["Mount Batur Sunrise", "Ubud Rice Terraces", "Beach Clubs", "Traditional Spa"],
                    includes: ["4-star resort", "Daily breakfast", "Airport transfers", "Cultural tours"]
                  }
                },
                {
                  name: "Iceland",
                  image: "https://images.unsplash.com/photo-1539066836270-648c8814f607?crop=entropy&cs=srgb&fm=jpg&ixid=M3w5fDB8MXxhbGx8fHx8fHx8fHwxNjk5NzE4ODcx&ixlib=rb-4.0.3&q=85&w=1600",
                  description: "For thrill-seekers craving the ultimate rush: ice caves, volcano hikes, and northern lights that'll push your limits beyond imagination",
                  popularity: "#2 Adventure",
                  price: "From $1299",
                  duration: "6 days",
                  details: {
                    duration: "6 days",
                    highlights: ["Northern Lights", "Blue Lagoon", "Golden Circle", "Glacier Hiking"],
                    includes: ["Boutique hotels", "4WD vehicle rental", "Thermal spa access", "Photography guide"]
                  }
                },
                {
                  name: "Morocco",
                  image: "https://images.unsplash.com/photo-1539650116574-75c0c6d73f6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w5fDB8MXxhbGx8fHx8fHx8fHwxNjk5NzE4ODcx&ixlib=rb-4.0.3&q=85&w=1600",
                  description: "Immerse yourself in ancient wisdom: navigate labyrinthine souks, learn centuries-old crafts, and sleep under Saharan stars like nomad ancestors",
                  popularity: "#3 Culture",
                  price: "From $899",
                  duration: "8 days",
                  details: {
                    duration: "8 days",
                    highlights: ["Marrakech Medina", "Sahara Desert", "Atlas Mountains", "Casablanca"],
                    includes: ["Riads & hotels", "Desert camping", "Camel trekking", "Local guide"]
                  }
                },
                {
                  name: "New Zealand",
                  image: "https://images.unsplash.com/photo-1507086364810-4d7b4c79629c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w5fDB8MXxhbGx8fHx8fHx8fHwxNjk5NzE4ODcx&ixlib=rb-4.0.3&q=85&w=1600",
                  description: "Breathtaking landscapes and adventure sports",
                  popularity: "#4 Nature",
                  price: "From $1599",
                  details: {
                    duration: "10 days",
                    highlights: ["Milford Sound", "Queenstown Adventures", "Rotorua Geothermals", "Hobbiton"],
                    includes: ["Premium lodges", "Rental car", "Adventure activities", "Scenic flights"]
                  }
                },
                {
                  name: "Japan",
                  image: "https://images.unsplash.com/photo-1618083707368-b3823daa2726?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxhZHZlbnR1cmV8ZW58MHx8fGJsdWV8MTc1MzIxOTI4N3ww&ixlib=rb-4.1.0&q=85",
                  description: "Ancient traditions meet modern innovation",
                  popularity: "#5 Culture",
                  price: "From $1899",
                  details: {
                    duration: "9 days",
                    highlights: ["Tokyo & Kyoto", "Mount Fuji", "Traditional Ryokans", "Bullet Train"],
                    includes: ["Ryokans & hotels", "JR Rail Pass", "Cultural experiences", "Temple visits"]
                  }
                },
                {
                  name: "Greece",
                  image: "https://images.unsplash.com/photo-1512757776214-26d36777b513?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHx0cmF2ZWx8ZW58MHx8fGJsdWV8MTc1MzIxOTI4MHww&ixlib=rb-4.1.0&q=85",
                  description: "Ancient history and stunning island life",
                  popularity: "#6 Romance",
                  price: "From $999",
                  details: {
                    duration: "7 days",
                    highlights: ["Santorini Sunsets", "Mykonos Beaches", "Athens History", "Island Hopping"],
                    includes: ["Boutique hotels", "Ferry passes", "Wine tastings", "Archaeological tours"]
                  }
                }
              ].map((destination, index) => (
                <div 
                  key={index} 
                  className="destination-card-enhanced"
                >
                  <div 
                    className="destination-card-image-bg"
                    style={{
                      backgroundImage: `url(${destination.image})`
                    }}
                    onClick={() => handleDestinationClick(destination, true)}
                  >
                  </div>
                  
                  <div className="destination-badges">
                    <div className="popularity-badge-enhanced">{destination.popularity}</div>
                    <div className="price-duration-badges">
                      <div className="price-badge-enhanced">{destination.price}</div>
                      <div className="duration-badge-enhanced">{destination.duration}</div>
                    </div>
                  </div>
                  
                  <div className="destination-card-content">
                    {/* Heart Icon moved to top right of content section */}
                    <button 
                      key={`dest-heart-${destination.name}`}
                      className={`favorite-heart-btn destination-content-heart ${isFavorite(destination) ? 'favorited' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        addToFavorites(destination);
                      }}
                      title={isFavorite(destination) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {isFavorite(destination) ? '❤️' : '🤍'}
                    </button>
                    <h3 className="destination-name">{destination.name}</h3>
                    <p className="destination-description">{destination.description}</p>
                    <button 
                      className="plan-trip-btn-golden" 
                      onClick={() => handleDestinationClick(destination, false)}
                    >
                      <span>💬</span>
                      Plan with Lauren
                    </button>
                  </div>
                </div>
              ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Enhanced Chat Screen Component - Device-aware layout
  const ChatScreen = () => {
    return (
      <div data-testid="chat-screen" className="chat-screen-wide" data-device={device.breakpoint}>
        <div className="chat-layout-panels">
          {/* Left: Main Chat Panel */}
          <div className="chat-panel-main">
            <div className="chat-panel-header">
              <div className="chat-panel-avatar">L</div>
              <div>
                <div className="chat-panel-name">Lauren</div>
                <div className="chat-panel-status">AI Travel Specialist</div>
              </div>
              <div className="chat-panel-online-dot" />
            </div>

            <div className="chat-panel-messages" ref={chatMessagesRef}>
              {messages.length === 0 ? (
                <div className="chat-panel-welcome">
                  <div className="cpw-avatar">L</div>
                  <h3 className="cpw-title">Hi, I'm Lauren</h3>
                  <p className="cpw-subtitle">Your AI travel specialist. Ask me anything about destinations, flights, hotels, or trip planning.</p>
                  <div className="cpw-quick-grid">
                    <button data-testid="quick-europe" className="cpw-card" onClick={() => sendMessage("I want to plan a trip to Europe")}>
                      <span className="cpw-card-icon">
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </span>
                      <span>Plan European Trip</span>
                    </button>
                    <button data-testid="quick-tropical" className="cpw-card" onClick={() => sendMessage("Find me a tropical getaway")}>
                      <span className="cpw-card-icon">
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                      </span>
                      <span>Tropical Escape</span>
                    </button>
                    <button data-testid="quick-flights" className="cpw-card" onClick={() => sendMessage("I need help with flights and hotels")}>
                      <span className="cpw-card-icon">
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                      </span>
                      <span>Flights + Hotels</span>
                    </button>
                    <button data-testid="quick-deals" className="cpw-card" onClick={() => sendMessage("What are the best travel deals right now?")}>
                      <span className="cpw-card-icon">
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </span>
                      <span>Find Deals</span>
                    </button>
                    <button data-testid="quick-japan" className="cpw-card" onClick={() => sendMessage("What's the best time to visit Japan?")}>
                      <span className="cpw-card-icon">
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      </span>
                      <span>Best time for Japan?</span>
                    </button>
                    <button data-testid="quick-honeymoon" className="cpw-card" onClick={() => sendMessage("Help me plan a honeymoon trip")}>
                      <span className="cpw-card-icon">
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                      </span>
                      <span>Plan a Honeymoon</span>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {messages.map(message => (
                    <div key={message.id} data-testid={`chat-msg-${message.sender}`} className={`chat-panel-msg ${message.sender}`}>
                      {message.sender === 'lauren' && <div className="chat-panel-msg-avatar">L</div>}
                      <div className="chat-panel-msg-body">
                        <div className="chat-panel-msg-text">
                          {message.sender === 'lauren' ? (
                            <ChatMessageContent content={message.message} />
                          ) : (
                            message.message
                          )}
                        </div>

                        {message.sender === 'lauren' && message.data && (
                          <>
                            {message.data.action === 'show_search_results' && message.data.search_results && (
                              <div className="booking-search-results">
                                <h4 className="results-title">Available Options:</h4>
                                {message.data.search_results.map((result, index) => (
                                  <div key={index} className="booking-option-card">
                                    <div className="option-header">
                                      <span className="option-number">Option {result.option_number}</span>
                                      <span className="option-price">${result.price || result.total_price} {result.currency}</span>
                                    </div>
                                    {message.data.booking_type === 'flight' && (
                                      <div className="flight-details">
                                        <p><strong>{result.airline}</strong></p>
                                        <p>Departure: {result.departure_time}</p>
                                        <p>Arrival: {result.arrival_time}</p>
                                        <p>Duration: {result.duration}</p>
                                        <p>Stops: {result.stops}</p>
                                      </div>
                                    )}
                                    {message.data.booking_type === 'hotel' && (
                                      <div className="hotel-details">
                                        <p><strong>{result.hotel_name}</strong> ({result.rating} stars)</p>
                                        <p>Room: {result.room_type}</p>
                                        <p>Per night: ${result.price_per_night} {result.currency}</p>
                                      </div>
                                    )}
                                    {message.data.booking_type === 'car' && (
                                      <div className="car-details">
                                        <p><strong>{result.provider}</strong></p>
                                        <p>Vehicle: {result.vehicle_model}</p>
                                        <p>Total: ${result.total_price} (${result.per_day_price}/day)</p>
                                        {result.features?.length > 0 && <p>Features: {result.features.join(', ')}</p>}
                                      </div>
                                    )}
                                    <button className="select-option-btn" onClick={() => sendMessage(`I'd like to book option ${result.option_number}`)}>Select This Option</button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {message.data.action === 'confirm_booking' && (
                              <div className="booking-confirmation-card">
                                <h4 className="confirmation-title">Booking Summary</h4>
                                <p><strong>Selected:</strong> {message.data.booking_type} booking</p>
                                <div className="confirmation-actions">
                                  <button className="confirm-booking-btn" onClick={() => sendMessage("Yes, please confirm this booking")}>Confirm Booking</button>
                                  <button className="cancel-booking-btn" onClick={() => sendMessage("No, I want to see different options")}>Change Selection</button>
                                </div>
                              </div>
                            )}
                            {message.data.action === 'reservation_ready' && (
                              <div className="reservation-success-card">
                                <h4 className="success-title">Reservation Created!</h4>
                                <p><strong>ID:</strong> {message.data.reservation_id}</p>
                                <p><strong>Total:</strong> ${message.data.total_price}</p>
                                <p><strong>Expires:</strong> {new Date(message.data.expires_at).toLocaleString()}</p>
                                <button className="proceed-payment-btn" onClick={() => sendMessage("Yes, let's proceed with payment")}>Proceed to Payment</button>
                              </div>
                            )}
                            {message.data.action === 'redirect_to_payment' && (
                              <div className="payment-redirect-card">
                                <h4 className="payment-title">Ready for Payment</h4>
                                <p><strong>Amount:</strong> ${message.data.payment_details?.amount}</p>
                                <p><strong>Description:</strong> {message.data.payment_details?.description}</p>
                                <button className="payment-btn" onClick={() => { setCurrentBookingStep('payment'); showNotification('Redirecting to secure payment...', 'info'); }}>Pay Securely</button>
                              </div>
                            )}
                            {message.data.action === 'trip_plan' && message.data.trip_plan?.is_diverse_comparison && (
                              <TripComparisonCard
                                tripPlan={message.data.trip_plan}
                                onSelectTrip={(opt) => sendMessage(`I'd like to plan a trip to ${opt.city}, ${opt.country}. Let's dive deeper into this option.`)}
                              />
                            )}
                            {message.data.action === 'trip_plan' && message.data.trip_plan && !message.data.trip_plan.is_diverse_comparison && (
                              <TripPlanCard
                                tripPlan={message.data.trip_plan}
                                sendMessage={sendMessage}
                              />
                            )}
                          </>
                        )}

                        <div className="chat-panel-msg-time">
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {message.sender === 'lauren' && message.responseTimeMs != null && (
                            <span data-testid="response-time-badge" className={`response-time-badge speed-${message.speedLabel || 'fast'}`}>
                              {message.speedLabel === 'instant' ? (
                                <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg> Instant</>
                              ) : message.speedLabel === 'fast' ? (
                                <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg> {(message.responseTimeMs / 1000).toFixed(1)}s</>
                              ) : message.speedLabel === 'researched' ? (
                                <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg> Researched {(message.responseTimeMs / 1000).toFixed(1)}s</>
                              ) : (
                                <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M12 6v6l4 2"/></svg> Deep research {(message.responseTimeMs / 1000).toFixed(0)}s</>
                              )}
                            </span>
                          )}
                        </div>
                        {message.workflow_id && (
                          <WorkflowTracker workflowId={message.workflow_id} authToken={authToken} />
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Chat Input */}
            <div className="chat-panel-input-area">
              <div className="chat-panel-input-row">
                <input
                  data-testid="chat-input"
                  type="text"
                  className="chat-panel-input"
                  placeholder="Ask Lauren anything about travel..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  ref={chatInputRef}
                />
                {/* Mic button removed — feature disabled for MVP streamlining */}
                <button
                  data-testid="chat-send-btn"
                  onClick={() => sendMessage()}
                  disabled={!inputMessage.trim() || isLoading}
                  className="chat-panel-send-btn"
                >
                  {isLoading ? (
                    <svg width="18" height="18" className="animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" opacity="0.75"/></svg>
                  ) : (
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                  )}
                </button>
              </div>
              {isLoading && (
                <div className="chat-panel-typing" data-testid="thinking-indicator">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: 'var(--gold-primary, #D4AF37)', fontWeight: 600, fontSize: '0.82rem' }}>Lauren is working</span>
                      <span className="typing-dot-anim"><span/><span/><span/></span>
                    </div>
                    <div className="pipeline-steps-live" data-testid="pipeline-steps-live" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {pipelineSteps.length > 0 ? (
                        pipelineSteps.map((s) => (
                          <span key={s.step} data-testid={`pipeline-step-${s.step}`} style={{
                            fontSize: '0.68rem',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.3s ease',
                            background: s.status === 'done' ? 'rgba(16, 185, 129, 0.12)' : 'rgba(212, 175, 55, 0.12)',
                            color: s.status === 'done' ? '#34d399' : '#d4af37',
                            border: `1px solid ${s.status === 'done' ? 'rgba(16, 185, 129, 0.25)' : 'rgba(212, 175, 55, 0.25)'}`,
                          }}>
                            {s.status === 'done' ? (
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                            ) : (
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1.2s linear infinite' }}><circle cx="12" cy="12" r="10" strokeDasharray="40" strokeDashoffset="10"/></svg>
                            )}
                            {s.detail || s.step}
                          </span>
                        ))
                      ) : (
                        <>
                          <span style={{ fontSize: '0.68rem', color: 'var(--text-dim, #64748b)', opacity: 0.8 }}>Analyzing your request...</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Trip Activity Panel — hidden on mobile (accessible via FAB) */}
          {!device.isMobile && (
            <TripActivityPanel messages={messages} authToken={authToken} isLoading={isLoading} />
          )}
        </div>

        {/* Mobile: Activity panel toggle FAB - only show when there are AI messages to review */}
        {device.isMobile && messages.length > 1 && (
          <button
            style={{
              display: 'flex', position: 'fixed', bottom: 'calc(64px + 70px)', right: '12px', zIndex: 90,
              width: '36px', height: '36px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #d4a054, #c4903c)',
              border: 'none', color: '#0f172a', cursor: 'pointer',
              boxShadow: '0 3px 10px rgba(212,160,84,0.3)',
              alignItems: 'center', justifyContent: 'center',
            }}
            onClick={() => setMobileActivityOpen(true)}
            data-testid="mobile-activity-fab"
            aria-label="View trip activity"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          </button>
        )}

        {/* Mobile: Activity panel bottom sheet overlay */}
        {device.isMobile && mobileActivityOpen && (
          <div className="trip-activity-panel-mobile-overlay" onClick={() => setMobileActivityOpen(false)}>
            <div onClick={e => e.stopPropagation()}>
              <button className="trip-panel-close" onClick={() => setMobileActivityOpen(false)} style={{
                position: 'fixed', top: '32vh', right: '12px', zIndex: 502,
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem',
              }}>&times;</button>
              <div style={{
                position: 'fixed', bottom: 0, left: 0, right: 0, maxHeight: '65vh',
                borderRadius: '1rem 1rem 0 0', overflow: 'auto', zIndex: 501,
                background: '#0f172a',
              }}>
                <TripActivityPanel messages={messages} authToken={authToken} isLoading={isLoading} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Search Screen Component
  const SearchScreen = () => (
    <SearchInterface 
      onSearch={handleSearch}
      onBackToHome={() => {
        setCurrentBookingStep('home');
        setCurrentView('welcome');
      }}
    />
  );

  // Results Screen Component
  const ResultsScreen = () => (
    <SearchResults 
      searchType={searchType}
      searchParams={searchParams}
      searchResults={searchResults}
      authToken={authToken}
      onBookItem={handleBookItem}
      onBackToSearch={() => setCurrentBookingStep('search')}
    />
  );

  // Cart Screen Component
  const CartScreen = () => (
    <ShoppingCart 
      items={cartItems}
      onRemoveItem={handleRemoveFromCart}
      onProceedToPayment={handleProceedToPayment}
      onBackToSearch={() => setCurrentBookingStep('search')}
    />
  );

  // Payment Screen Component
  const PaymentScreen = () => (
    <PaymentForm 
      cartItems={cartItems}
      onPaymentSuccess={handlePaymentSuccess}
      onBackToCart={() => setCurrentBookingStep('cart')}
    />
  );

  // Confirmation Screen Component
  const ConfirmationScreen = () => (
    <BookingConfirmation 
      confirmation={bookingConfirmation}
      onBackToHome={handleBackToHome}
    />
  );

  // Enhanced Favorites Screen Component with Real API Integration
  const FavoritesScreen = () => {
    const [apiFavorites, setApiFavorites] = useState([]);
    const [loadingFavs, setLoadingFavs] = useState(true);

    useEffect(() => {
      loadFavorites();
    }, []);

    const loadFavorites = async () => {
      try {
        setLoadingFavs(true);
        const userId = currentUser?.id || 'guest_user';
        const response = await fetch(`${API}/user/${userId}/favorites`);
        const data = await response.json();
        
        // Format for display
        const formatted = data.map(fav => ({
          id: fav.id,
          name: fav.trip_name,
          image: fav.image_url,
          notes: fav.description,
          rating: 5,
          category: 'Adventure',
          budget: 'Mid-range',
          visited: false,
          priority: 'normal'
        }));
        
        setApiFavorites(formatted);
      } catch (error) {
        console.error('Error loading favorites:', error);
        setApiFavorites(favorites); // Fallback to existing favorites
      } finally {
        setLoadingFavs(false);
      }
    };

    const handleRemoveFavorite = async (favoriteId) => {
      try {
        const userId = currentUser?.id || 'guest_user';
        const response = await fetch(`${API}/user/${userId}/favorites/${favoriteId}`, {
          method: 'DELETE'
        });
        const result = await response.json();
        
        if (result.success) {
          // Reload favorites
          loadFavorites();
          // Also update local state
          removeFavorite(favoriteId);
        }
      } catch (error) {
        console.error('Error removing favorite:', error);
      }
    };

    const displayFavorites = apiFavorites.length > 0 ? apiFavorites : favorites;

    if (loadingFavs) {
      return (
        <div className="favorites-screen">
          <div style={{padding: '4rem', textAlign: 'center', color: 'white'}}>
            <div className="spinner" style={{margin: '0 auto 1rem'}}></div>
            <p>Loading your favorites...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="favorites-screen">
        <div className="favorites-hero">
          <div className="favorites-hero-content">
            <h1>✨ Your Dream Destinations</h1>
            <p>Curated collection of places that captured your heart</p>
            <div className="favorites-stats">
              <div className="stat-item">
                <span className="stat-number">{displayFavorites.length}</span>
                <span className="stat-label">Saved</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{displayFavorites.filter(f => f.visited).length}</span>
                <span className="stat-label">Visited</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{displayFavorites.filter(f => !f.visited).length}</span>
                <span className="stat-label">Wishlist</span>
              </div>
            </div>
          </div>
        </div>
      
      {displayFavorites.length > 0 ? (
        <div className="favorites-content">
          <div className="favorites-grid-enhanced">
            {displayFavorites.map(favorite => (
              <div key={favorite.id} className="favorite-card-enhanced">
                <div className="favorite-image-bg" style={{
                  backgroundImage: `url(${favorite.image || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800'})`
                }}>
                  <div className="favorite-overlay">
                    <div className="favorite-badges">
                      {favorite.visited && <span className="visited-badge">✅ Visited</span>}
                      {favorite.priority === 'high' && <span className="priority-badge">⭐ Priority</span>}
                    </div>
                    <button className="remove-favorite-btn-enhanced" onClick={() => handleRemoveFavorite(favorite.id)}>
                      <span>❤️</span>
                    </button>
                  </div>
                </div>
                
                <div className="favorite-content-enhanced">
                  <div className="favorite-header-enhanced">
                    <h3>{favorite.name}</h3>
                    <div className="favorite-rating">
                      {'★'.repeat(favorite.rating || 5)}
                    </div>
                  </div>
                  
                  <p className="favorite-description">{favorite.notes || "A destination close to your heart"}</p>
                  
                  <div className="favorite-details">
                    <span className="favorite-category">🌍 {favorite.category || 'Adventure'}</span>
                    <span className="favorite-budget">💰 {favorite.budget || 'Mid-range'}</span>
                  </div>
                  
                  <div className="favorite-actions-enhanced">
                    <button className="book-favorite-btn-enhanced" onClick={() => {
                      setCurrentView('chat');
                      setTimeout(() => {
                        sendMessage(`I want to plan a trip to ${favorite.name}. Can you help me create an amazing itinerary?`);
                      }, 500);
                    }}>
                      <span>🗓️</span>
                      Plan Trip
                    </button>
                    <button className="share-favorite-btn-enhanced" onClick={() => shareFavorite(favorite)}>
                      <span>📤</span>
                      Share
                    </button>
                    <button className="chat-favorite-btn-enhanced" onClick={() => {
                      setCurrentView('chat');
                      setTimeout(() => {
                        sendMessage(`Tell me more about ${favorite.name}. What makes it special?`);
                      }, 500);
                    }}>
                      <span>💬</span>
                      Ask Lauren
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-favorites-enhanced">
          <div className="empty-favorites-animation">
            <div className="empty-heart">💖</div>
            <div className="empty-sparkles">✨</div>
          </div>
          <h3>Your heart awaits adventure</h3>
          <p>Discover breathtaking destinations and save the ones that speak to your soul</p>
          <div className="empty-favorites-actions">
            <button 
              className="explore-destinations-btn-enhanced"
              onClick={() => setCurrentView('welcome')}
            >
              <span>🌟</span>
              Explore Destinations
            </button>
            <button 
              className="chat-lauren-btn-enhanced"
              onClick={() => {
                setCurrentView('chat');
                setTimeout(() => {
                  sendMessage("Hi Lauren! I'm looking for some amazing destinations to add to my favorites. What would you recommend based on popular trends?");
                }, 500);
              }}
            >
              <span>💬</span>
              Ask Lauren for Ideas
            </button>
          </div>
        </div>
      )}
    </div>
    );
  };

  // Helper functions for favorites with improved state management
  const addToFavorites = (destination) => {
    
    if (!destination) {
      console.error("No destination provided to addToFavorites");
      return;
    }
    
    // Create a unique identifier
    const destinationId = destination.id || destination.title || destination.name;
    
    // Check if already in favorites using the unique identifier
    const isAlreadyFavorite = favorites.some(fav => 
      fav.id === destinationId || 
      fav.title === destinationId || 
      fav.name === destinationId
    );
    
    if (isAlreadyFavorite) {
      // Remove from favorites
      const newFavorites = favorites.filter(fav => 
        fav.id !== destinationId && 
        fav.title !== destinationId && 
        fav.name !== destinationId
      );
      setFavorites(newFavorites);
      localStorage.setItem('favorites', JSON.stringify(newFavorites));
    } else {
      // Add to favorites
      const favoriteItem = {
        id: destinationId,
        name: destination.name || destination.title,
        title: destination.title || destination.name,
        image: destination.image || destination.featured_image,
        description: destination.description,
        price: destination.price || destination.price_from,
        duration: destination.duration || destination.duration_days,
        destination: destination.destination,
        details: destination.details,
        highlights: destination.highlights,
        addedAt: new Date().toISOString()
      };
      const newFavorites = [...favorites, favoriteItem];
      setFavorites(newFavorites);
      localStorage.setItem('favorites', JSON.stringify(newFavorites));
    }
  };

  const isFavorite = (destination) => {
    if (!destination) {
      console.warn("No destination provided to isFavorite");
      return false;
    }
    
    const destinationId = destination.id || destination.title || destination.name;
    const result = favorites.some(fav => 
      fav.id === destinationId || 
      fav.title === destinationId || 
      fav.name === destinationId
    );
    
    return result;
  };

  const removeFavorite = (favoriteId) => {
    const newFavorites = favorites.filter(f => f.id !== favoriteId);
    setFavorites(newFavorites);
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
  };

  const shareFavorite = (favorite) => {
    if (navigator.share) {
      navigator.share({
        title: `Check out ${favorite.name}!`,
        text: `I found this amazing destination on Uncharted Travels: ${favorite.name}`,
        url: window.location.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      const shareText = `Check out ${favorite.name} on Uncharted Travels! ${window.location.href}`;
      navigator.clipboard.writeText(shareText);
      showToast('Link copied to clipboard!', 'success');
    }
  };

  // Settings Screen Component with Enhanced Functionality
  const SettingsScreen = () => {
    const [tempNotifications, setTempNotifications] = useState({
      email: true,
      deals: true
    });
    const [settingsChanged, setSettingsChanged] = useState(false);

    const handleNotificationChange = (type, value) => {
      setTempNotifications(prev => ({
        ...prev,
        [type]: value
      }));
      setSettingsChanged(true);
    };

    const saveSettings = async () => {
      try {
        showToast("Settings saved!", "success");
        setSettingsChanged(false);
      } catch (error) {
        showToast("Failed to save settings.", "error");
      }
    };

    const resetSettings = () => {
      setTempNotifications({
        email: true,
        deals: true
      });
      setSettingsChanged(false);
    };

    const themeIcons = { light: '\u2600', dark: '\uD83C\uDF19', brand: '\uD83C\uDFAF', explorer: '\u26FA' };

    return (
      <div className="settings-screen">
        <div className="screen-header">
          <h1 data-testid="settings-title">Settings</h1>
          <p>Customize your travel experience</p>
        </div>
        
        <div className="settings-sections">
          <div className="settings-section">
            <h3>Appearance</h3>
            <div className="setting-item">
              <label>{t('theme', 'Theme')}</label>
              <div className="theme-selector" data-testid="theme-selector">
                {['light', 'dark', 'brand', 'explorer'].map(theme => (
                  <button
                    key={theme}
                    data-testid={`theme-${theme}-btn`}
                    className={`theme-option ${currentTheme === theme ? 'active' : ''}`}
                    onClick={() => {
                      setCurrentTheme(theme);
                      localStorage.setItem('app_theme', theme);
                      setSettingsChanged(true);
                    }}
                  >
                    {themeIcons[theme]} {theme.charAt(0).toUpperCase() + theme.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3>Language</h3>
            <div className="setting-item">
              <label>{t('language', 'Language')}</label>
              <select
                data-testid="language-selector"
                className="setting-select"
                value={language}
                onChange={(e) => {
                  changeLanguage(e.target.value);
                  setSettingsChanged(true);
                }}
                style={{ width: '100%' }}
              >
                {Object.entries(languages).map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="settings-section">
            <h3>Profile</h3>
            <div className="setting-item">
              <label>{t('username', 'Username')}</label>
              <input 
                type="text" 
                value={currentUser?.username || ''} 
                disabled={!currentUser}
                className="setting-input"
                placeholder="Enter your username"
              />
            </div>
            <div className="setting-item">
              <label>{t('email', 'Email')}</label>
              <input 
                type="email" 
                value={currentUser?.email || ''} 
                disabled={!currentUser}
                className="setting-input"
                placeholder="Enter your email"
              />
            </div>
            {!currentUser && (
              <div className="settings-note">
                <p><strong>Create an account</strong> to customize your profile settings</p>
              </div>
            )}
          </div>

          <div className="settings-section">
            <h3>Notifications</h3>
            <div className="setting-item">
              <div className="setting-toggle">
                <label>Email notifications</label>
                <input 
                  type="checkbox" 
                  checked={tempNotifications.email}
                  onChange={(e) => handleNotificationChange('email', e.target.checked)}
                />
              </div>
            </div>
            <div className="setting-item">
              <div className="setting-toggle">
                <label>Deal alerts</label>
                <input 
                  type="checkbox" 
                  checked={tempNotifications.deals}
                  onChange={(e) => handleNotificationChange('deals', e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3>Travel Preferences</h3>
            <div className="setting-item">
              <label>{t('preferred_currency', 'Preferred Currency')}</label>
              <select className="setting-select">
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="CAD">CAD - Canadian Dollar</option>
              </select>
            </div>
            <div className="setting-item">
              <label>Default Trip Duration</label>
              <select className="setting-select">
                <option value="short">Short trips (1-3 days)</option>
                <option value="medium">Medium trips (4-7 days)</option>
                <option value="long">Long trips (8+ days)</option>
              </select>
            </div>
          </div>

          <div className="settings-section">
            <h3>Travel Tools</h3>
            <div className="setting-item">
              <button data-testid="open-currency-alerts" className="settings-action-btn" onClick={() => setCurrentView('currency-alerts')}>
                Currency Rate Alerts
              </button>
            </div>
            <div className="setting-item">
              <button data-testid="open-visa-prefill" className="settings-action-btn" onClick={() => setCurrentView('visa-prefill')}>
                Visa Pre-fill
              </button>
            </div>
          </div>

          <div className="settings-section">
            <h3>Notifications</h3>
            <div className="setting-item">
              <button className="settings-action-btn" onClick={() => setCurrentView('notification-center')} data-testid="open-notification-center">
                View All Notifications
              </button>
            </div>
            <div className="setting-item">
              <button className="settings-action-btn" onClick={() => setCurrentView('notification-settings')} data-testid="open-notification-prefs">
                Notification Preferences
              </button>
            </div>
          </div>

          <div className="settings-section">
            <h3>Account & Security</h3>
            <div className="setting-item">
              <button className="settings-action-btn">
                Change Password
              </button>
            </div>
            <div className="setting-item">
              <button className="settings-action-btn">
                Two-Factor Authentication
              </button>
            </div>
            <div className="setting-item">
              <button className="settings-action-btn">
                Download My Data
              </button>
            </div>
            {isAuthenticated && (
              <div className="setting-item">
                <button 
                  className="settings-logout-btn"
                  onClick={() => {
                    localStorage.removeItem('auth_token');
                    setIsAuthenticated(false);
                    setCurrentUser(null);
                    setAuthToken(null);
                    setCurrentView('welcome');
                    showToast('Logged out successfully', 'success');
                  }}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Settings Action Buttons */}
        {settingsChanged && (
          <div className="settings-actions">
            <div className="settings-save-bar">
              <p>You have unsaved changes</p>
              <div className="settings-buttons">
                <button className="settings-reset-btn" onClick={resetSettings}>
                  {t('reset', 'Reset')}
                </button>
                <button className="settings-save-btn" onClick={saveSettings}>
                  {t('save_changes', 'Save Changes')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Admin Screen Component (if user has admin access) - Now using dedicated component
  const AdminScreen = () => <AdminDashboard currentUser={currentUser} />;

  // Detailed Trip Screen Component
  // NOTE: Remove accidental duplicated inline TripDetailsScreen to avoid conflicts
  // Keeping the imported TripDetailsScreen from ./TripDetailsScreen only.
  const TripDetailsScreenInline = () => {
    if (!selectedDestination) return null;

    return (
      <div className="trip-details-screen">
        <div className="trip-details-container">
          <div className="trip-details-header">
            <button 
              className="back-btn-enhanced"
              onClick={() => setCurrentView('welcome')}
            >
              ← Back to Destinations
            </button>
            <h1 className="trip-details-title">{selectedDestination.name}</h1>
          </div>
          
          <div 
            className="trip-details-hero"
            style={{
              '--trip-bg-image': `url(${selectedDestination.image})`
            }}
          >
            <div className="trip-hero-content">
              <div className="trip-badges">
                <span className="popularity-badge">{selectedDestination.popularity}</span>
                <span className="price-badge-large">{selectedDestination.price}</span>
              </div>
              <h2>{selectedDestination.details.duration}</h2>
              <p>{selectedDestination.description}</p>
            </div>
          </div>

          <div className="trip-details-content">
            <div className="trip-highlights">
              <h3>✨ Trip Highlights</h3>
              <div className="highlights-grid">
                {selectedDestination.details.highlights.map((highlight, index) => (
                  <div key={index} className="highlight-card">
                    <span className="highlight-icon">🌟</span>
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="trip-includes">
              <h3>📦 What's Included</h3>
              <div className="includes-list">
                {selectedDestination.details.includes.map((item, index) => (
                  <div key={index} className="include-item">
                    <span className="check-icon">✅</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="trip-actions">
              <button 
                className="book-trip-btn"
                onClick={() => {
                  setCurrentView('chat');
                  setTimeout(() => {
                    sendMessage(`I'd like to book the ${selectedDestination.name} trip. Can you help me with the details?`);
                  }, 500);
                }}
              >
                <span>🎯</span>
                Book This Trip
              </button>
              <button 
                className="chat-about-trip-btn"
                onClick={() => {
                  setCurrentView('chat');
                  setTimeout(() => {
                    sendMessage(`Tell me more about the ${selectedDestination.name} trip. I have some questions.`);
                  }, 500);
                }}
              >
                <span>💬</span>
                Ask Lauren
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render function
  // ── Admin subdomain surface (admin.unchartedtravel.agency) ──
  // Renders ONLY the admin login → Command Center. No public marketing site,
  // no customer navigation. Authorization is enforced server-side on every API call.
  if (ADMIN_HOST) {
    return (
      <div className="app admin-surface" data-device={device.breakpoint} data-testid="admin-surface">
        <ConnectionBanner />
        {toasts.length > 0 && (
          <div data-testid="toast-container" style={{
            position: 'fixed', top: 20, right: 20, zIndex: 10000, display: 'flex',
            flexDirection: 'column', gap: 8, maxWidth: 380,
          }}>
            {toasts.map(t => (
              <div key={t.id} data-testid={`toast-${t.type}`} style={{
                padding: '12px 20px', borderRadius: 10,
                background: t.type === 'error' ? 'rgba(239,68,68,0.95)' : t.type === 'success' ? 'rgba(16,185,129,0.95)' : t.type === 'warning' ? 'rgba(245,158,11,0.95)' : 'rgba(59,130,246,0.95)',
                color: '#fff', fontSize: 14, fontWeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                backdropFilter: 'blur(8px)',
              }}>{t.message}</div>
            ))}
          </div>
        )}
        <Suspense fallback={<LazyFallback />}>
          {!isAuthenticated ? (
            <LoginScreen
              loginForm={loginForm}
              setLoginForm={setLoginForm}
              login={login}
              authError={authError}
              authLoading={authLoading}
              showToast={showToast}
              setCurrentView={setCurrentView}
            />
          ) : ['admin', 'dev'].includes(currentUser?.role) ? (
            <CommandCenter currentUser={currentUser} onExit={logout} />
          ) : (
            <div data-testid="admin-access-denied" style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              minHeight: '100vh', minHeight: '100dvh', background: '#0a0f1a', color: '#e2e8f0',
              fontFamily: 'system-ui, -apple-system, sans-serif', padding: 24, textAlign: 'center',
            }}>
              <h1 style={{ color: '#d4af37', fontSize: '1.5rem', marginBottom: 12 }}>Access Denied</h1>
              <p style={{ color: '#94a3b8', maxWidth: 420, lineHeight: 1.6 }}>
                This area is restricted to Uncharted Travels administrators. Your account does not have access.
              </p>
              <button
                onClick={logout}
                style={{ marginTop: 20, padding: '12px 32px', background: '#d4af37', color: '#0a0f1a', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}
              >Sign in with a different account</button>
            </div>
          )}
        </Suspense>
      </div>
    );
  }

  return (
    <div className="app" data-device={device.breakpoint}>
      <ConnectionBanner />
      {isAuthenticated && <NotificationToast userId={currentUser?.user_id || currentUser?.id} />}
      
      {/* Toast Notifications (#4) */}
      {toasts.length > 0 && (
        <div data-testid="toast-container" style={{
          position: 'fixed', top: 20, right: 20, zIndex: 10000, display: 'flex',
          flexDirection: 'column', gap: 8, maxWidth: 380,
        }}>
          {toasts.map(t => (
            <div key={t.id} data-testid={`toast-${t.type}`} style={{
              padding: '12px 20px', borderRadius: 10,
              background: t.type === 'error' ? 'rgba(239,68,68,0.95)' : t.type === 'success' ? 'rgba(16,185,129,0.95)' : t.type === 'warning' ? 'rgba(245,158,11,0.95)' : 'rgba(59,130,246,0.95)',
              color: '#fff', fontSize: 14, fontWeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              backdropFilter: 'blur(8px)', animation: 'slideInRight 0.3s ease',
            }}>
              {t.message}
            </div>
          ))}
        </div>
      )}

      {/* Session Expiry Warning (#31) */}
      {sessionWarning === 'expiring' && (
        <div data-testid="session-warning" style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: 'rgba(245,158,11,0.95)', color: '#fff', textAlign: 'center',
          padding: '8px 16px', fontSize: 13, fontWeight: 600,
        }}>
          Your session is expiring soon. Save your work.
        </div>
      )}
      {MainNavigation()}
      {MobileMenuOverlay()}
      
      <main className="main-content" data-device={device.breakpoint}>
        <Suspense fallback={<LazyFallback />}>
        {/* Route based on currentView and currentBookingStep - Only render one view at a time */}
        {currentView === 'login' ? (
          <LoginScreen
            loginForm={loginForm}
            setLoginForm={setLoginForm}
            login={login}
            authError={authError}
            authLoading={authLoading}
            showToast={showToast}
            setCurrentView={setCurrentView}
          />
        ) : currentView === 'signup' ? (
          <SignupScreen
            signupForm={signupForm}
            setSignupForm={setSignupForm}
            signup={signup}
            authError={authError}
            authLoading={authLoading}
            signupSuccess={signupSuccess}
            setCurrentView={setCurrentView}
          />
        ) : currentView === 'payment-success' ? (
          <PaymentSuccess
            sessionIdParam={new URLSearchParams(window.location.search).get('session_id')}
            onNavigateHome={() => { window.history.replaceState({}, '', '/'); setCurrentView('welcome'); setCurrentBookingStep('home'); }}
            onNavigateChat={() => { window.history.replaceState({}, '', '/'); setCurrentView('chat'); }}
          />
        ) : currentView === 'payment-cancel' ? (
          <PaymentCancel
            onNavigateHome={() => { window.history.replaceState({}, '', '/'); setCurrentView('welcome'); setCurrentBookingStep('home'); }}
            onNavigateChat={() => { window.history.replaceState({}, '', '/'); setCurrentView('chat'); }}
            onRetryBooking={() => { window.history.replaceState({}, '', '/'); setCurrentView('welcome'); setCurrentBookingStep('search'); }}
          />
        ) : currentView === 'chat' ? (
          isAuthenticated ? (
            ChatScreen()
          ) : (
            <div className="chat-screen" style={{ marginTop: '80px', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
              {/* Show the chat UI header and empty messages area */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="var(--gold-primary)" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <h2 data-testid="chat-paywall-title" style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 700, margin: '20px 0 8px' }}>
                  Meet Lauren, your AI travel agent
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', maxWidth: '400px', lineHeight: 1.6, textAlign: 'center', margin: '0 0 28px' }}>
                  Lauren can search flights, book trips, and plan your entire journey. Sign in to start chatting.
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    data-testid="chat-login-btn"
                    onClick={() => setCurrentView('login')}
                    style={{
                      padding: '12px 32px', background: 'var(--gold-primary)', color: 'var(--btn-primary-text)',
                      border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
                    }}
                  >
                    Sign In
                  </button>
                  <button
                    data-testid="chat-signup-btn"
                    onClick={() => setCurrentView('signup')}
                    style={{
                      padding: '12px 32px', background: 'transparent', color: 'var(--gold-primary)',
                      border: '2px solid var(--gold-primary)', borderRadius: '10px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
                    }}
                  >
                    Create Account
                  </button>
                </div>
              </div>
            </div>
          )
        ) : currentView === 'favorites' ? (
          <FavoritesScreen />
        ) : currentView === 'trips' ? (
          <TravelerDashboard
            authToken={authToken}
            currentUser={currentUser}
            onNavigateToChat={(msg) => {
              setCurrentView('chat');
              setCurrentBookingStep('home');
              if (msg) setTimeout(() => sendMessage(msg), 400);
            }}
            onNavigateToTrip={(trip) => {
              setSelectedTrip(trip);
              setShowTripDetails(true);
            }}
          />
        ) : currentView === 'settings' ? (
          <SettingsScreen />
        ) : currentView === 'contact' ? (
          <ContactScreen
            contactForm={contactForm}
            setContactForm={setContactForm}
            submitContactForm={submitContactForm}
            isLoading={isLoading}
            setCurrentView={setCurrentView}
          />
        ) : currentView === 'welcome' && currentBookingStep === 'home' ? (
          WelcomeScreen()
        ) : currentBookingStep === 'search' ? (
          SearchScreen()
        ) : currentBookingStep === 'results' ? (
          ResultsScreen()
        ) : null}
        
        {/* Booking steps are now rendered as full-screen overlays to avoid background bleed-through */}
        {/* {currentBookingStep === 'cart' && <CartScreen />} */}
        {/* {currentBookingStep === 'payment' && <PaymentScreen />} */}
        {/* {currentBookingStep === 'confirmation' && <ConfirmationScreen />} */}
        {showTripDetails && selectedTrip ? (
          <TripDetailsScreen 
            destination={selectedTrip}
            onBack={closeTripDetails}
            onChat={(dest) => {
              setCurrentView('chat');
              setTimeout(() => sendMessage(`Tell me more about ${dest.title || dest.name}`), 300);
            }}
            onBook={handleBookTrip}
            authToken={authToken}
          />
        ) : null}
        {currentView === 'trip-details' && (
          <TripDetailsScreen 
            destination={selectedDestination}
            onBack={() => {
              setCurrentView('welcome');
              setShowPopularDestinations(true);
            }}
            onChat={(destination) => {
              setCurrentView('chat');
              setTimeout(() => {
                sendMessage(`I'm interested in learning more about ${destination.title || destination.name}. Can you help me plan this trip?`);
              }, 500);
            }}
            onBook={(destination) => {
              setCurrentView('welcome');
              setCurrentBookingStep('search');
            }}
            authToken={authToken}
          />
        )}

        {/* Booking Flow Rendering */}
        {currentView === 'welcome' && currentBookingStep === 'cart' && (
          <div className="booking-flow-overlay">
            <div className="booking-flow-container">
              <button className="booking-flow-close" onClick={() => setCurrentBookingStep('home')}>✕</button>
              <h2>Your Trip</h2>
              <ShoppingCart 
                cartItems={cartItems}
                authToken={authToken}
                onRemoveItem={handleRemoveFromCart}
                onProceedToPayment={(totalAmount, hasInsurance) => setCurrentBookingStep('payment')}
              />
            </div>
          </div>
        )}

        {currentView === 'welcome' && currentBookingStep === 'payment' && (
          <div className="booking-flow-overlay">
            <div className="booking-flow-container">
              <button className="booking-flow-close" onClick={() => setCurrentBookingStep('cart')}>← Back</button>
              <h2>Payment</h2>
              <PaymentForm 
                cartItems={cartItems}
                onPaymentSuccess={handlePaymentSuccess}
                onBackToCart={() => setCurrentBookingStep('cart')}
              />
            </div>
          </div>
        )}

        {currentView === 'welcome' && currentBookingStep === 'confirmation' && bookingConfirmation && (
          <div className="booking-flow-overlay">
            <div className="booking-flow-container">
              <BookingConfirmation 
                confirmation={bookingConfirmation}
                onBackToHome={handleBackToHome}
              />
            </div>
          </div>
        )}
        {currentView === 'admin' && currentUser?.role && ['admin', 'dev', 'customer_service'].includes(currentUser.role) && AdminScreen()}

        {/* Command Center */}
        {currentView === 'command-center' && currentUser?.role && ['admin', 'dev'].includes(currentUser.role) && (
          <CommandCenter
            currentUser={currentUser}
            onExit={() => setCurrentView('welcome')}
          />
        )}

        {/* Currency Rate Alerts */}
        {currentView === 'currency-alerts' && (
          <CurrencyAlertPanel
            userId={currentUser?.user_id || currentUser?.id || 'guest'}
            onClose={() => setCurrentView('settings')}
          />
        )}

        {/* Visa Pre-fill */}
        {currentView === 'visa-prefill' && (
          <VisaPrefillPanel
            userId={currentUser?.user_id || currentUser?.id || 'guest'}
            onClose={() => setCurrentView('settings')}
          />
        )}
        
        {/* Notification Settings */}
        {currentView === 'notification-settings' && (
          <div style={{ padding: '24px 16px', maxWidth: '600px', margin: '0 auto' }}>
            <NotificationPreferences
              userId={currentUser?.user_id || currentUser?.id || 'guest'}
              onClose={() => setCurrentView('home')}
            />
          </div>
        )}

        {/* Notification Center */}
        {currentView === 'notification-center' && (
          <NotificationCenter
            userId={currentUser?.user_id || currentUser?.id || 'guest'}
            onClose={() => setCurrentView('settings')}
          />
        )}
        
        {/* User Account Modal */}
        {showUserAccount && (
          <UserAccount 
            onClose={() => setShowUserAccount(false)}
            userId={currentUser?.id || 'guest_user'}
          />
        )}
        {showSupportCenter && (
          <SupportCenter 
            authToken={authToken}
            onClose={() => setShowSupportCenter(false)}
          />
        )}
        </Suspense>
      </main>
      
      {/* Hide footer on login/signup screens */}
      {currentView !== 'login' && currentView !== 'signup' && currentView !== 'chat' && <Footer />}
      {MobileBottomNav()}

      {/* Onboarding Flow (#20) */}
      {showOnboarding && (
        <div data-testid="onboarding-overlay" style={{
          position: 'fixed', inset: 0, zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
        }}>
          <div style={{
            background: 'linear-gradient(145deg, rgba(15,25,40,0.98), rgba(10,18,30,0.99))',
            borderRadius: 20, padding: '32px 28px', maxWidth: 420, width: '90%',
            border: '1px solid rgba(212,160,84,0.2)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}>
            {[
              { title: 'Meet Lauren, Your AI Travel Agent', desc: 'Chat with Lauren to plan your perfect trip. She finds the best flights, hotels, and activities within your budget.', icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' },
              { title: 'Smart Price Protection', desc: 'Our system finds the cheapest options and protects against dynamic pricing. You always get the best deal.', icon: 'M12 15V3m0 12l-4-4m4 4l4-4M2 17l.621 2.485A2 2 0 0 0 4.561 21h14.878a2 2 0 0 0 1.94-1.515L22 17' },
              { title: 'Book Activities On-Demand', desc: 'Plan your daily itinerary and only pay when you confirm. No upfront risk, maximum flexibility.', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
              { title: 'Group Travel & Real-Time Updates', desc: 'Plan trips with friends, track flight status in real-time, and share trip plans with a link.', icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zm13 0v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75' },
            ][onboardingStep] && (() => {
              const step = [
                { title: 'Meet Lauren, Your AI Travel Agent', desc: 'Chat with Lauren to plan your perfect trip. She finds the best flights, hotels, and activities within your budget.', icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' },
                { title: 'Smart Price Protection', desc: 'Our system finds the cheapest options and protects against dynamic pricing. You always get the best deal.', icon: 'M12 15V3m0 12l-4-4m4 4l4-4M2 17l.621 2.485A2 2 0 0 0 4.561 21h14.878a2 2 0 0 0 1.94-1.515L22 17' },
                { title: 'Book Activities On-Demand', desc: 'Plan your daily itinerary and only pay when you confirm. No upfront risk, maximum flexibility.', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                { title: 'Group Travel & Real-Time Updates', desc: 'Plan trips with friends, track flight status in real-time, and share trip plans with a link.', icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zm13 0v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75' },
              ][onboardingStep];
              return (
                <>
                  <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d4a054" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={step.icon}/></svg>
                  </div>
                  <h3 style={{ color: '#f0e6d3', fontSize: 20, fontWeight: 700, textAlign: 'center', margin: '0 0 10px' }}>{step.title}</h3>
                  <p style={{ color: '#a0aec0', fontSize: 14, textAlign: 'center', lineHeight: 1.6, margin: '0 0 24px' }}>{step.desc}</p>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 20 }}>
                    {[0,1,2,3].map(i => (
                      <div key={i} style={{ width: onboardingStep === i ? 24 : 8, height: 8, borderRadius: 4, background: onboardingStep === i ? '#d4a054' : 'rgba(255,255,255,0.1)', transition: 'all 0.3s' }} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                    <button onClick={dismissOnboarding} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#64748b', fontSize: 13, cursor: 'pointer' }}>Skip</button>
                    <button onClick={() => onboardingStep < 3 ? setOnboardingStep(onboardingStep + 1) : dismissOnboarding()} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#d4a054', color: '#0f1928', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      {onboardingStep < 3 ? 'Next' : 'Get Started'}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

function AppWrapper() {
  return (
    <DeviceProvider>
      <I18nProvider>
        <App />
      </I18nProvider>
    </DeviceProvider>
  );
}

export default AppWrapper;